/*
  Print product PDP behavior (shared by every product.*-pdp template that uses
  the Design selector block: business cards, post cards, flyers, raffle tickets, ...)

  1. Relocates the Uploadly app block into the "Upload Your Own Artwork" panel.
  2. Relocates the PitchPrint button section into the "Use a Template" panel.
  3. When "Let Us Design For You" is selected, adds the design-fee product to the
     cart (with the contact-form answers as line item properties) before letting
     the theme's normal add-to-cart flow run for the main product.
  4. Opens the design-examples dialog from the ? beside the design-service price.
     This is display-only and does not change cart or pricing behavior.
*/
(function () {
  'use strict';

  var RELOCATE_TIMEOUT_MS = 30000;
  var RELOCATE_POLL_MS = 300;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* Poll for an element until found (or timeout), then run callback. */
  function waitFor(getter, callback) {
    var elapsed = 0;
    var timer = setInterval(function () {
      var el = getter();
      if (el) {
        clearInterval(timer);
        callback(el);
      } else if ((elapsed += RELOCATE_POLL_MS) >= RELOCATE_TIMEOUT_MS) {
        clearInterval(timer);
      }
    }, RELOCATE_POLL_MS);
  }

  function getSelectedDesign(selector) {
    var checked = selector.querySelector('input[data-bc-design-option]:checked');
    return checked ? checked.value : 'template';
  }

  /* ---- Part 2: Uploadly relocation ---- */
  function relocateUploadly(selector) {
    var slot = selector.querySelector('[data-bc-uploadly-slot]');
    if (!slot) return;
    waitFor(
      function () {
        var el = document.querySelector(
          '.m-main-product div[id^="shopify-block-"][id*="uploadly"]'
        );
        // Wait until the app has actually rendered its upload UI inside the block
        return el && el.childElementCount > 0 ? el : null;
      },
      function (el) {
        slot.appendChild(el);
        el.classList.add('bc-relocated');
      }
    );
  }

  /* ---- Part 3: PitchPrint relocation ---- */
  function relocatePitchPrint(selector) {
    var slot = selector.querySelector('[data-bc-pitchprint-slot]');
    if (!slot) return;
    waitFor(
      function () {
        return document.getElementById('pp_main_btn_sec');
      },
      function (el) {
        slot.appendChild(el);
        el.classList.add('bc-relocated');
      }
    );
  }

  /* ---- Part 4: design-fee line item ---- */
  function collectProperties(selector) {
    var properties = {};
    var productTitle = selector.getAttribute('data-product-title');
    if (productTitle) properties['For Product'] = productTitle;
    selector.querySelectorAll('[data-bc-property]').forEach(function (field) {
      var value = (field.value || '').trim();
      if (value) properties[field.getAttribute('data-bc-property')] = value;
    });
    return properties;
  }

  function validateCustomFields(selector) {
    var valid = true;
    var firstInvalid = null;
    selector.querySelectorAll('[data-bc-required]').forEach(function (field) {
      if ((field.value || '').trim() === '') {
        field.classList.add('bc-invalid');
        if (!firstInvalid) firstInvalid = field;
        valid = false;
      } else {
        field.classList.remove('bc-invalid');
      }
    });
    var error = selector.querySelector('[data-bc-form-error]');
    if (error) error.hidden = valid;
    if (firstInvalid) firstInvalid.focus();
    return valid;
  }

  function addFeeProduct(selector) {
    var feeVariantId = parseInt(selector.getAttribute('data-fee-variant-id'), 10);
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: feeVariantId,
            quantity: 1,
            properties: collectProperties(selector),
          },
        ],
      }),
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (data) {
          throw new Error((data && data.description) || 'Could not add the design service to the cart.');
        });
      }
      return response.json();
    });
  }

  function bindAddToCart(selector) {
    var feeVariantId = parseInt(selector.getAttribute('data-fee-variant-id'), 10);
    if (!feeVariantId) return;

    // Capture on document so this runs before the theme's product-form submit
    // handler, which is bound directly on the form element.
    document.addEventListener(
      'submit',
      function (evt) {
        var form = evt.target;
        if (!form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
        if (form.dataset.bcFeePending === 'done') {
          delete form.dataset.bcFeePending;
          return; // re-dispatched submit: let the theme handle the main product
        }
        if (getSelectedDesign(selector) !== 'custom') return;

        evt.preventDefault();
        evt.stopImmediatePropagation();

        if (!validateCustomFields(selector)) return;

        var submitButton = form.querySelector('[name="add"]');
        if (submitButton) submitButton.classList.add('m-spinner-loading');

        addFeeProduct(selector)
          .then(function () {
            form.dataset.bcFeePending = 'done';
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit();
            } else {
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          })
          .catch(function (error) {
            var errorBox = selector.querySelector('[data-bc-form-error]');
            if (errorBox) {
              errorBox.textContent = error.message;
              errorBox.hidden = false;
            }
          })
          .finally(function () {
            if (submitButton) submitButton.classList.remove('m-spinner-loading');
          });
      },
      true
    );
  }

  function bindFieldCleanup(selector) {
    selector.addEventListener('input', function (evt) {
      if (evt.target.matches && evt.target.matches('[data-bc-required]')) {
        if ((evt.target.value || '').trim() !== '') evt.target.classList.remove('bc-invalid');
      }
    });
  }

  function bindExamplesModal(selector) {
    var trigger = selector.querySelector('[data-bc-examples-open]');
    var dialog = selector.querySelector('[data-bc-examples-dialog]');
    if (!trigger || !dialog || typeof dialog.showModal !== 'function') return;

    function closeDialog() {
      if (dialog.open) dialog.close();
    }

    trigger.addEventListener('click', function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (!dialog.open) dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');
    });

    var closeBtn = dialog.querySelector('[data-bc-examples-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (evt) {
        evt.preventDefault();
        closeDialog();
      });
    }

    dialog.addEventListener('click', function (evt) {
      if (evt.target === dialog) closeDialog();
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && dialog.open) closeDialog();
    });

    dialog.addEventListener('close', function () {
      trigger.setAttribute('aria-expanded', 'false');
    });

    dialog.addEventListener('cancel', function () {
      trigger.setAttribute('aria-expanded', 'false');
    });

    fillReviewQuotes(dialog);
  }

  function reviewText(review) {
    var description = review && review.description;
    if (!description) return '';
    if (typeof description.en === 'string') return description.en;
    if (description.en && description.en.text) return description.en.text;
    return '';
  }

  function reviewStars(rating) {
    var rounded = Math.round(rating || 0);
    var stars = '';
    for (var i = 1; i <= 5; i++) {
      stars += i <= rounded ? '★' : '<span class="gmr-star--off">★</span>';
    }
    return '<span class="gmr-stars">' + stars + '</span>';
  }

  function reviewDate(iso) {
    if (!iso) return '';
    var date = new Date(iso);
    if (isNaN(date)) return '';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function renderGoogleReview(review) {
    var text = reviewText(review);
    var avatar = review.profile_picture
      ? '<img class="gmr-review__avatar" src="' + escapeHtml(review.profile_picture) + '" alt="" loading="lazy">'
      : '<div class="gmr-review__avatar"></div>';
    return (
      '<div class="gmr-review">' +
      '<div class="gmr-review__head">' +
      avatar +
      '<div class="gmr-review__meta">' +
      '<div class="gmr-review__author">' + escapeHtml(review.author) + '</div>' +
      '<div class="gmr-review__date">' + escapeHtml(reviewDate(review.review_date)) + '</div>' +
      '</div></div>' +
      '<div class="gmr-review__stars" aria-label="' + escapeHtml(Math.round(review.rating || 0)) + ' out of 5 stars">' +
      reviewStars(review.rating) +
      '</div>' +
      (text ? '<p class="gmr-review__text">' + escapeHtml(text) + '</p>' : '') +
      '</div>'
    );
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function fillReviewQuotes(dialog) {
    var list = dialog.querySelector('[data-bc-examples-quotes]');
    var url = dialog.getAttribute('data-reviews-url');
    if (!list || !url || list.getAttribute('data-bc-reviews-loaded')) return;
    list.setAttribute('data-bc-reviews-loaded', 'pending');

    fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('reviews');
        return response.json();
      })
      .then(function (reviews) {
        if (!Array.isArray(reviews)) return;
        var wanted = [
          'Courtney Davis',
          'Danielle Lawrence',
          'James Lawrence',
          'Eric Sulivant',
          'Ali Egan',
          'Daniel Arnold',
          'Josh W',
          'Jo Mcarthur'
        ];
        var byName = {};
        reviews.forEach(function (review) {
          if (review && review.author && review.rating >= 5) byName[review.author] = review;
        });
        var picked = [];
        wanted.forEach(function (name) {
          var review = byName[name];
          var text = reviewText(review);
          if (text) picked.push(review);
        });
        if (!picked.length) return;
        list.innerHTML = picked.map(renderGoogleReview).join('');
        list.setAttribute('data-bc-reviews-loaded', 'true');
      })
      .catch(function () {
        list.removeAttribute('data-bc-reviews-loaded');
      });
  }

  ready(function () {
    var selector = document.querySelector('[data-bc-design-selector]');
    if (!selector) return;

    relocateUploadly(selector);
    relocatePitchPrint(selector);
    bindAddToCart(selector);
    bindFieldCleanup(selector);
    bindExamplesModal(selector);
  });
})();
